import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { PolicyService } from '@core/services/policy.service';
import { SpinnerService } from '@core/services/spinner.service';
import { TypeSafeFormGroup } from '@yourcause/common/core-forms';
import { I18nService } from '@yourcause/common/i18n';
import { uniqBy } from 'lodash';
import { SystemTagsService } from '../system-tags.service';
import { SystemTags } from '../typings/system-tags.typing';
import { ArrayHelpersService } from '@yourcause/common/utils';
import { TypeaheadSelectOption } from '@yourcause/common/core-forms';
export interface TagsGroup {
  tags: number[];
  newTagName: string;
}

@Component({
  selector: 'gc-system-tags-input',
  templateUrl: './system-tags-input.component.html',
  styleUrls: ['./system-tags-input.component.scss']
})
export class SystemTagsInputComponent implements OnInit {
  @Input() tagType: SystemTags.Buckets = SystemTags.Buckets.Global; //default tagType set to Global
  @Input() subTagType: SystemTags.Buckets;
  @Input() isOptional: boolean;
  @Input() controlName = 'tags';
  @Input() isNomination = false;
  @Input() isBulk = false;
  @Input() showLabel = true;
  @Input() formGroup: TypeSafeFormGroup<TagsGroup>;
  @Input() availableTags: TypeaheadSelectOption[];
  @Input() showHelp = true;
  @Output() onAddNewTagToggle = new EventEmitter<boolean>();
  @Output() onChange = new EventEmitter<number[]>();

  addingTag = false;
  canCreateTags = this.policyService.system.canManageTags();
  bucketOptions: TypeaheadSelectOption<SystemTags.Buckets>[] = [];
  addToBucketId: SystemTags.Buckets = SystemTags.Buckets.Global; //default bucket id set to Global

  constructor (
    private systemTagsService: SystemTagsService,
    private i18n: I18nService,
    private spinnerService: SpinnerService,
    private arrayHelper: ArrayHelpersService,
    private policyService: PolicyService
  ) { }

  ngOnInit () {
    this.availableTags = this.availableTags || this.getAvailableTags();
  }

  getAvailableTags () {
    const tagsForBucket = this.systemTagsService.getTagsForBucket(this.tagType);
    const subTags = this.subTagType ? this.systemTagsService.getTagsForBucket(this.subTagType) : [];
    const availableTags = uniqBy([
      ...tagsForBucket,
      ...subTags
    ], 'id').map((tag) => {
      return {
        label: tag.name,
        value: tag.id
      };
    });

    return this.arrayHelper.sort(availableTags, 'label');
  }

  setAddingTag (addingTag: boolean) {
    this.addingTag = addingTag;
    this.onAddNewTagToggle.emit(this.addingTag);
  }

  onAddTag () {
    this.setAddingTag(true);
    this.setBucketOptions();
  }

  onClearAddTag () {
    this.setAddingTag(false);
    this.setBucketOptions();
    this.clearNewTagName();
  }

  clearNewTagName () {
    this.formGroup.get('newTagName').setValue('');
  }

  async onSaveNewTag () {
    const foundExistingTag = this.availableTags.find((tag) => {
      return tag.label.toLowerCase() === this.formGroup.value.newTagName.toLowerCase();
    });
    if (foundExistingTag) {
      this.onClearAddTag();
      this.addNewAvailableTag(foundExistingTag.value);
    } else {
      this.spinnerService.startSpinner();
      const tagId = await this.systemTagsService.addRecord({
        name: this.formGroup.value.newTagName,
        description: '',
        color: SystemTags.TagColor.Yellow,
        bucketId: this.addToBucketId,
        enabled: true,
        active: true
      });
      if (tagId) {
        this.onClearAddTag();
        this.addNewAvailableTag(tagId);
      }
      this.spinnerService.stopSpinner();
    }
  }

  addNewAvailableTag (tagId: number) {
    const newTag = this.systemTagsService.findTag(tagId);
    if (newTag) {
      this.availableTags = this.arrayHelper.sort(
        [
          ...this.availableTags,
          {
            label: newTag.name,
            value: newTag.id
          }
        ],
        'label'
      );
      setTimeout(() => {
        this.formGroup.get('tags').setValue(
          this.formGroup.value.tags.concat(tagId)
        );
      });
    }
  }

  setBucketOptions () {
    this.bucketOptions = this.arrayHelper.sort([
      {
        value: SystemTags.Buckets.Global,
        label: this.i18n.translate('tags:lblGlobal', {}, 'Global')
      },
      this.getTagTypeFromBucket(this.tagType),
      this.getTagTypeFromBucket(this.subTagType)
    ].filter((item) => !!item), 'label');
    this.addToBucketId = this.tagType;
  }

  getTagTypeFromBucket (tagType: SystemTags.Buckets) {
    switch (tagType) {
      case SystemTags.Buckets.Application:
        return {
          value: SystemTags.Buckets.Application,
          label: this.i18n.translate('common:lblApplication', {}, 'Application')
         };
      case SystemTags.Buckets.Nomination:
        return {
          value: SystemTags.Buckets.Nomination,
          label: this.i18n.translate('FORMS:textNomination', {}, 'Nomination')
        };
      case SystemTags.Buckets.NonprofitProfile:
        return {
          value: SystemTags.Buckets.NonprofitProfile,
          label: this.i18n.translate('tags:lblNonprofitProfile', {}, 'Nonprofit profile')
        };
      case SystemTags.Buckets.Payment:
        return {
          value: SystemTags.Buckets.Payment,
          label: this.i18n.translate('common:lblPayment', {}, 'Payment')
        };
      case SystemTags.Buckets.Applicant:
        return {
          value: SystemTags.Buckets.Applicant,
          label: this.i18n.translate('common:lblApplicant', {}, 'Applicant')
        };
      case SystemTags.Buckets.Budget:
        return {
          value: SystemTags.Buckets.Budget,
          label: this.i18n.translate('common:lblBudget', {}, 'Budget')
        };
    default:
     return null;
   }
  }

  bucketChange (bucket: SystemTags.Buckets) {
    this.addToBucketId = bucket;
  }

}
